// WebSocket hook for chess engine communication

import { useEffect, useRef, useState, useCallback } from 'react';
import { GameState, WebSocketMessage } from '../types';
import { api } from '../utils/api';

export function useChessEngine(gameId: string | null) {
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!gameId) return;

    const wsUrl = api.getWebSocketUrl(gameId);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket connected');
      setConnected(true);
      setError(null);
    };

    ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        
        switch (message.type) {
          case 'game_update':
            setGameState(message.data as GameState);
            break;
          
          case 'game_over':
            setGameState(prev => prev ? {
              ...prev,
              status: 'finished',
              result: message.data.result,
              result_reason: message.data.reason,
            } : null);
            break;
          
          case 'error':
            setError(message.data.message);
            console.error('Game error:', message.data.message);
            break;
        }
      } catch (err) {
        console.error('Error parsing WebSocket message:', err);
      }
    };

    ws.onerror = (event) => {
      console.error('WebSocket error:', event);
      setError('Connection error');
      setConnected(false);
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setConnected(false);
    };

    return () => {
      ws.close();
    };
  }, [gameId]);

  const sendMove = useCallback((uci: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    const message: WebSocketMessage = {
      type: 'move',
      data: { uci },
    };

    wsRef.current.send(JSON.stringify(message));
  }, []);

  return {
    gameState,
    connected,
    error,
    sendMove,
  };
}
